import React, { useMemo } from "react";
import RevenueBarChart from "./RevenueBarChart";
import FinancialGraph from "./FinancialGraph";
import { useOperationsKpis } from "../../hooks/useOperationsKpis";
import { useDailyOrgMetrics } from "../../hooks/useDailyOrgMetrics";
import type { MetricSample } from "../../hooks/useMetricSamples";
import { useData } from "../../hooks/useData";
import { monthToDateRange, orgTodayKey } from "../../lib/portalItems";

// =============================================================================
// CashHero — True-embed widget (TE-F3)
// Owns its own data fetch via canonical primitives (useOperationsKpis +
// useDailyOrgMetrics). Caller passes orgId/dateRange/optional cosmetics only.
// Renders byte-identical to the BentoMetrics.tsx hero section so admin and
// portal baselines match exactly (Step 5 visual regression check).
// =============================================================================

interface CashHeroProps {
  orgId: string | null;
  dateRange: { start: Date | null; end: Date | null };
  goalAmount?: number;
  daysElapsed?: number;
  daysInMonth?: number;
  getSamples?: (key: string) => MetricSample[];
}

const CashHero: React.FC<CashHeroProps> = ({
  orgId,
  dateRange,
  goalAmount,
  daysElapsed,
  daysInMonth,
  getSamples,
}) => {
  const { kpis, isLoading: kpisLoading } = useOperationsKpis(orgId, dateRange);
  const {
    data: dailyChartData,
    isLoading: dailyLoading,
    granularity: chartGranularity,
  } = useDailyOrgMetrics(orgId, dateRange);
  // The goal is a MONTH goal, so it counts this month's cash in org tz, whatever the
  // picker says. Before CO13 (2026-10-07) it used the picker's cash (default last 30
  // days): Rayston saw 55% of $500k on Oct 7 when October so far was $36,976.
  const { currentOrganization } = useData();
  const todayKey = orgTodayKey(currentOrganization?.timezone ?? null);
  const monthRange = useMemo(() => monthToDateRange(todayKey), [todayKey]);
  const hasGoal = (goalAmount ?? 0) > 0;
  const { kpis: monthKpis, isLoading: monthLoading } = useOperationsKpis(
    hasGoal ? orgId : null,
    monthRange
  );
  const isLoading = kpisLoading || dailyLoading || (hasGoal && monthLoading);

  return (
    <div
      data-testid="cash-hero"
      className="flex flex-col lg:flex-row gap-3 items-stretch overflow-hidden animate-rise animate-rise-1"
    >
      <div className="flex-shrink-0 w-full lg:w-auto">
        {isLoading ? (
          <div className="liquid-glass-hero h-[280px] anim-skeleton" />
        ) : (
          <RevenueBarChart
            revenueClosed={kpis.revenueClosed}
            cashCollected={kpis.cashCollected}
            getSamples={getSamples}
            highTicketCash={kpis.highTicketCash}
            lowTicketCash={kpis.lowTicketCash}
            scoLowTicketCash={kpis.scoLowTicketCash}
            installmentsCash={kpis.installmentsCash}
            goalAmount={goalAmount}
            goalCash={monthKpis.cashCollected}
            daysElapsed={daysElapsed}
            daysInMonth={daysInMonth}
          />
        )}
      </div>
      <div className="flex-1 min-w-0 min-h-[260px] overflow-hidden">
        {isLoading ? (
          <div className="liquid-glass h-full anim-skeleton" />
        ) : (
          <FinancialGraph
            data={dailyChartData}
            goalAmount={goalAmount}
            daysInMonth={daysInMonth}
            granularity={chartGranularity}
          />
        )}
      </div>
    </div>
  );
};

export default CashHero;
